import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  Avatar,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const TeamMembersPage = () => {
  const navigate = useNavigate();

  // Local state
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/users`);
      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get role color
  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "error";
      case "developer":
        return "primary";
      case "tester":
        return "success";
      case "viewer":
        return "default";
      default:
        return "default";
    }
  };

  // Helper function to get initials
  const getInitials = (username) => {
    if (!username) return "U";
    const parts = username.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return username.substring(0, 2).toUpperCase();
  };

  // Filter users based on search
  const filteredUsers = users.filter(
    (u) =>
      (u.username || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.role || "").toLowerCase().includes(search.toLowerCase())
  );

  // Calculate stats
  const totalUsers = users.length;
  const adminCount = users.filter((u) => u.role?.toLowerCase() === "admin").length;
  const developerCount = users.filter((u) => u.role?.toLowerCase() === "developer").length;

  const statCards = [
    { title: "Total Members", value: totalUsers },
    { title: "Admins", value: adminCount },
    { title: "Developers", value: developerCount },
    { title: "Active", value: totalUsers },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f9fafb", px: 4, py: 3 }}>
      {/* HEADER */}
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/projects")}
          sx={{
            textTransform: "none",
            color: "#0094FB",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "rgba(0, 148, 251, 0.08)",
            },
          }}
        >
          Back to Projects
        </Button>
      </Box>

      <Typography variant="h5" fontWeight={700}>
        Team Members
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Manage your team members and their roles
      </Typography>

      {/* SEARCH BAR */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search Members"
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Stats Row */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        {statCards.map((stat, i) => (
          <Grid key={i} item xs={6} md={3}>
            <Card
              elevation={0}
              sx={{
                width: 260,
                height: 72,
                borderRadius: "12px",
                border: "1px solid #D9D9D9",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "#FFFFFF",
              }}
            >
              <Typography fontSize="14px" color="#344054">
                {stat.title}
              </Typography>
              <Typography fontSize="22px" fontWeight={400} mt={0.5}>
                {stat.value}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* USER CARDS */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <Typography color="text.secondary">
            {search ? "No members found matching your search" : "No members found."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} md={6} key={user.id}>
              <Card
                sx={{
                  borderRadius: 2,
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
                  p: 2.5,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Box display="flex" alignItems="start" gap={2}>
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: "#0094FB",
                      fontSize: "18px",
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(user.username)}
                  </Avatar>

                  {/* User Info */}
                  <Box flex={1}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="start"
                      mb={1}
                    >
                      <Box>
                        <Typography fontWeight={600} fontSize="16px">
                          {user.username}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <EmailIcon sx={{ fontSize: 14, color: "#757575" }} />
                          <Typography fontSize="13px" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Role Chip */}
                      <Chip
                        label={user.role || "Member"}
                        color={getRoleColor(user.role)}
                        size="small"
                        sx={{ fontWeight: 600, textTransform: "capitalize" }}
                      />
                    </Box>

                    {/* Member Since */}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      mt={1}
                      pt={1}
                      borderTop="1px solid #f0f0f0"
                    >
                      <PersonIcon sx={{ fontSize: 14, color: "#757575" }} />
                      <Typography fontSize="12px" color="text.secondary">
                        Member since{" "}
                        {new Date(user.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default TeamMembersPage;
